import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import i18n from 'i18next';

import API from '../api/api.js';
import { trackEvent } from '../analytics.js';
import './TravelataAccommodation.css';

function buildQuery(travel) {
  const city = travel.city.name_translations.en;
  const countryCode = travel.city.country_code || travel.airport?.country_code || '';
  const days = moment(travel.return_date).diff(moment(travel.depart_date), 'days');

  return {
    city,
    country_code: countryCode,
    city_code: travel.city.code || travel.destination,
    origin: travel.origin,
    depart: moment(travel.depart_date).format('YYYY-MM-DD'),
    return: moment(travel.return_date).format('YYYY-MM-DD'),
    days: Math.max(1, days),
  };
}

function formatRub(price) {
  if (price == null) {
    return '';
  }
  try {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: 'RUB',
      maximumFractionDigits: 0,
    }).format(price);
  } catch (e) {
    return `${price} ₽`;
  }
}

function renderTourList(travel, tours, searchPageUrl, t) {
  return (
    <div className="travelata-accommodation">
      <ul className="travelata-accommodation__list">
        {tours.map((tour) => (
          <li key={`${tour.hotelName}-${tour.checkinDate}-${tour.price}`} className="travelata-accommodation__item">
            <a
              className="travelata-accommodation__card"
              href={tour.tourPageUrl}
              target="_blank"
              rel="noopener noreferrer sponsored"
              onClick={() => {
                trackEvent('travelata_click', {
                  destination_iata: travel.destination,
                  hotel: tour.hotelName,
                  price_rub: tour.price,
                });
              }}
            >
              {tour.hotelPreview && (
                <img
                  className="travelata-accommodation__image"
                  src={tour.hotelPreview}
                  alt=""
                  loading="lazy"
                />
              )}
              <div className="travelata-accommodation__body">
                <div className="travelata-accommodation__title-row">
                  <strong className="travelata-accommodation__name">{tour.hotelName}</strong>
                  {tour.hotelCategoryName && (
                    <span className="travelata-accommodation__stars">{tour.hotelCategoryName}</span>
                  )}
                </div>
                <div className="travelata-accommodation__meta">
                  {tour.hotelRating && (
                    <span className="travelata-accommodation__rating">
                      {t('travelCard.travelataRating', { rating: tour.hotelRating })}
                    </span>
                  )}
                  <span className="travelata-accommodation__dates">
                    {t('travelCard.travelataCheckin', { date: tour.checkinDate })}
                    {' · '}
                    {t('travelCard.travelataNights', { count: tour.nights })}
                  </span>
                </div>
                <div className="travelata-accommodation__price-row">
                  <span className="travelata-accommodation__price">{formatRub(tour.price)}</span>
                  <span className="travelata-accommodation__cta">{t('travelCard.travelataViewDeal')}</span>
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>
      {searchPageUrl && (
        <a
          className="travelata-accommodation__more"
          href={searchPageUrl}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={() => {
            trackEvent('travelata_search_click', {
              destination_iata: travel.destination,
            });
          }}
        >
          {t('travelCard.travelataSeeAll')}
        </a>
      )}
      <p className="travelata-accommodation__note">{t('travelCard.travelataNote')}</p>
    </div>
  );
}

function TravelataAccommodation({ travel, children }) {
  const t = i18n.t.bind(i18n);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!travel?.city) {
      setData(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const query = buildQuery(travel);

    setLoading(true);
    API.fetchTravelataAccommodation(query)
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData({ success: false, tours: [], searchPageUrl: 'https://travelata.ru/' });
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [travel?.id, travel?.depart_date, travel?.return_date]);

  if (!travel?.city || typeof children !== 'function') {
    return null;
  }

  const tours = data?.tours || [];
  const hasTours = tours.length > 0;
  const searchPageUrl = data?.searchPageUrl || 'https://travelata.ru/';

  return children({
    loading,
    hasTours,
    searchPageUrl,
    list: hasTours ? renderTourList(travel, tours, searchPageUrl, t) : null,
  });
}

TravelataAccommodation.propTypes = {
  travel: PropTypes.object,
  children: PropTypes.func.isRequired,
};

export default TravelataAccommodation;
